import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useDispatch, useSelector } from 'react-redux';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import type {
    ICampaignHostBillingHistoryItem,
    ICampaignHostSubscription,
} from '../../../types/global/campaignHostSubscription.types';
import {
    formatBillingHistoryAmount,
    formatSubscriptionDate,
    formatSubscriptionDisplayPrice,
    isLifetimeSubscription,
    subscriptionPriceIntervalLabel,
} from '../../../utils/campaignHostSubscriptionFormat';
import {
    BRAND_LUXE_ERROR,
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_LOW,
    BRAND_SURFACE_CONTAINER_LOWEST,
} from '../brandDashboardTokens';
import { campaignHostBillingHistorySelector } from '../../../redux/campaignHostSubscription/selectors';
import {
    cancelCampaignHostSubscription,
    fetchCampaignHostBillingHistory,
    fetchCampaignHostMine,
    fetchCampaignHostCreationAccess,
} from '../../../redux/campaignHostSubscription/actions';
import { useDispatchPromise } from '../../../utils/AppUtils';
import { getActiveSubscriptionFeatureRows } from './campaignHostTierFeatures';
import { CampaignHostSuiteFeatureList } from './CampaignHostSuiteFeatureList';
import { MIXPANEL_EVENT, trackSubscriptionAction } from '../../../analytics/Mixpanel';

const subscriptionDialogPaperSx = {
    bgcolor: BRAND_SURFACE_CONTAINER_LOW,
    backgroundImage: 'none',
    borderRadius: 3,
    border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
};

const subscriptionDialogTitleSx = {
    fontFamily: FONT_ENUM.MAIN_BOLD,
    fontSize: 20,
    color: '#fff',
    pb: 1,
};

const subscriptionDialogBodySx = {
    fontFamily: FONT_ENUM.MAIN_MEDIUM,
    fontSize: 14,
    color: 'rgba(255,255,255,0.75)',
    lineHeight: 1.6,
};

const subscriptionDialogActionsSx = {
    px: 3,
    pb: 3,
    pt: 0,
    gap: 1,
    flexWrap: 'wrap' as const,
    justifyContent: 'flex-end',
};

export interface CampaignHostSubscriptionActiveViewProps {
    subscription: ICampaignHostSubscription;
    onManageBilling: () => void;
}

export function CampaignHostSubscriptionActiveView({
    subscription,
    onManageBilling,
}: CampaignHostSubscriptionActiveViewProps) {
    const dispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();
    const billingHistory = useSelector(campaignHostBillingHistorySelector);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [canceling, setCanceling] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);

    const isLtd = isLifetimeSubscription(subscription);
    const previewItems = useMemo(() => {
        const fromStripe = subscription.stripe_details?.payment_history ?? [];
        const fromPaystack = subscription.paystack_details?.payment_history ?? [];
        const merged = fromStripe.length ? fromStripe : fromPaystack;
        return merged.slice(0, 5);
    }, [subscription]);

    const allHistoryItems: ICampaignHostBillingHistoryItem[] = billingHistory?.items ?? previewItems;

    const openFullHistory = () => {
        dispatch(fetchCampaignHostBillingHistory({ subscriptionId: subscription._id, limit: 50 }));
        setHistoryOpen(true);
    };

    const openInvoices = () => {
        const items: ICampaignHostBillingHistoryItem[] =
            subscription.stripe_details?.payment_history ?? subscription.paystack_details?.payment_history ?? [];
        const withUrl = items.filter(i => i.hosted_invoice_url || i.invoice_pdf);
        if (!withUrl.length) {
            openFullHistory();
            return;
        }
        withUrl.forEach(i => {
            const url = i.hosted_invoice_url ?? i.invoice_pdf;
            if (url) window.open(url, '_blank', 'noopener,noreferrer');
        });
    };

    const handleCancel = async (cancelImmediately: boolean) => {
        trackSubscriptionAction(MIXPANEL_EVENT.PROCEED_WITH_CANCEL_SUBSCRIPTION, {
            buttonName: cancelImmediately ? 'CANCEL NOW' : 'END OF PERIOD',
            buttonAction: 'PROCEED_WITH_CANCEL_SUBSCRIPTION',
            subscriptionType: 'campaign_host',
            cancelImmediately,
            subscriptionId: subscription._id,
        });
        setCanceling(true);
        try {
            await dispatchPromise(
                cancelCampaignHostSubscription({
                    subscriptionId: subscription._id,
                    cancelImmediately,
                }),
            );
            setCancelOpen(false);
            dispatch(fetchCampaignHostMine());
            dispatch(fetchCampaignHostCreationAccess());
            dispatch(fetchCampaignHostBillingHistory({ subscriptionId: subscription._id, limit: 50 }));
        } finally {
            setCanceling(false);
        }
    };

    const pkg = subscription.package;
    const featureRows = getActiveSubscriptionFeatureRows(isLtd);

    return (
        <Box sx={{ maxWidth: 640, mx: 'auto' }}>
            <Box sx={{ position: 'relative', mb: 5 }}>
                <Box
                    sx={{
                        position: 'absolute',
                        inset: -2,
                        borderRadius: 3,
                        background: `linear-gradient(90deg, ${BRAND_LUXE_PRIMARY}, #feb700, ${BRAND_LUXE_PRIMARY})`,
                        opacity: 0.2,
                        filter: 'blur(8px)',
                    }}
                />
                <Box
                    sx={{
                        position: 'relative',
                        p: 4,
                        borderRadius: 3,
                        bgcolor: BRAND_SURFACE_CONTAINER,
                        border: `1px solid ${BRAND_LUXE_PRIMARY}33`,
                        overflow: 'hidden',
                    }}>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -80,
                            right: -80,
                            width: 160,
                            height: 160,
                            borderRadius: '50%',
                            bgcolor: `${BRAND_LUXE_PRIMARY}14`,
                        }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                        <Box>
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    px: 1.5,
                                    py: 0.5,
                                    mb: 1.5,
                                    borderRadius: 99,
                                    bgcolor: `${BRAND_LUXE_SECONDARY}18`,
                                    border: `1px solid ${BRAND_LUXE_SECONDARY}33`,
                                }}>
                                <VerifiedIcon sx={{ fontSize: 14, color: BRAND_LUXE_SECONDARY }} />
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: BRAND_LUXE_SECONDARY,
                                        letterSpacing: '0.1em',
                                    }}>
                                    ACTIVE
                                </Typography>
                            </Box>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 32,
                                    fontWeight: 800,
                                    color: '#fff',
                                    lineHeight: 1.1,
                                }}>
                                {pkg?.name ?? 'Your Plan'}
                            </Typography>
                            <Typography sx={{ color: '#adaaaa', mt: 0.5 }}>
                                {pkg?.description ?? 'Campaign host membership'}
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography
                                sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 24, color: BRAND_LUXE_PRIMARY }}>
                                {formatSubscriptionDisplayPrice(subscription)}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: '#adaaaa', letterSpacing: '0.06em' }}>
                                {subscriptionPriceIntervalLabel(subscription)}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                        {!isLtd && (
                            <Button
                                onClick={() => {
                                    trackSubscriptionAction(MIXPANEL_EVENT.MANAGE_BILLING_CLICK, {
                                        buttonName: 'MANAGE BILLING',
                                        buttonAction: 'MANAGE_BILLING',
                                        subscriptionType: 'campaign_host',
                                        subscriptionId: subscription._id,
                                    });
                                    onManageBilling();
                                }}
                                sx={{
                                    py: 1.25,
                                    px: 4,
                                    borderRadius: 99,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    color: '#392700',
                                    background: `linear-gradient(135deg, ${BRAND_LUXE_PRIMARY} 0%, #feb700 100%)`,
                                }}>
                                Manage Billing
                            </Button>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#adaaaa' }}>
                            <CalendarTodayIcon sx={{ fontSize: 18, color: BRAND_LUXE_PRIMARY }} />
                            <Typography sx={{ fontSize: 14 }}>
                                Subscribed {formatSubscriptionDate(subscription.createdAt)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, mb: 2, px: 1 }}>
                Included with {pkg?.name ?? 'your plan'}
            </Typography>
            <Box sx={{ mb: 5 }}>
                <CampaignHostSuiteFeatureList rows={featureRows} variant="active" />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 1 }}>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18 }}>Billing History</Typography>
                <Button
                    onClick={openFullHistory}
                    sx={{
                        color: BRAND_LUXE_PRIMARY,
                        textTransform: 'none',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 14,
                    }}>
                    View All
                </Button>
            </Box>
            <Box sx={{ borderRadius: 3, overflow: 'hidden', bgcolor: BRAND_SURFACE_CONTAINER_LOWEST, mb: 5 }}>
                {previewItems.length === 0 ? (
                    <Typography sx={{ p: 3, color: '#adaaaa', textAlign: 'center' }}>No payments yet</Typography>
                ) : (
                    previewItems.map((item, idx) => (
                        <Box
                            key={item.id}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 2,
                                borderBottom:
                                    idx < previewItems.length - 1 ? `1px solid ${BRAND_OUTLINE_VARIANT}22` : 'none',
                            }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <ReceiptLongIcon sx={{ color: '#adaaaa' }} />
                                <Box>
                                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                                        {item.description ?? 'Payment'}
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, color: '#adaaaa' }}>
                                        {formatSubscriptionDate(item.date)}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD }}>
                                    {formatBillingHistoryAmount(item)}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: BRAND_LUXE_SECONDARY,
                                        textTransform: 'uppercase',
                                    }}>
                                    {item.status}
                                </Typography>
                            </Box>
                        </Box>
                    ))
                )}
            </Box>

            <Box
                sx={{
                    p: 3,
                    borderRadius: 3,
                    bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                    border: `1px solid ${BRAND_OUTLINE_VARIANT}33`,
                    textAlign: 'center',
                }}>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16, mb: 1 }}>
                    Membership Options
                </Typography>
                <Typography sx={{ color: '#adaaaa', fontSize: 14, mb: 3, maxWidth: 360, mx: 'auto' }}>
                    {isLtd
                        ? 'You have lifetime access. Contact support for billing questions.'
                        : 'Update your card or cancel at the end of your billing period.'}
                </Typography>
                <Button
                    onClick={openInvoices}
                    sx={{ color: '#adaaaa', textTransform: 'none', fontSize: 13, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                    Download Tax Invoices
                </Button>
                {!isLtd && (
                    <>
                        <Box
                            sx={{
                                width: 48,
                                height: 1,
                                bgcolor: BRAND_OUTLINE_VARIANT,
                                opacity: 0.3,
                                mx: 'auto',
                                my: 2,
                            }}
                        />
                        <Button
                            onClick={() => {
                                trackSubscriptionAction(MIXPANEL_EVENT.CANCEL_SUBSCRIPTION_CLICK, {
                                    buttonName: 'CANCEL SUBSCRIPTION',
                                    buttonAction: 'CANCEL_SUBSCRIPTION',
                                    subscriptionType: 'campaign_host',
                                    subscriptionId: subscription._id,
                                });
                                setCancelOpen(true);
                            }}
                            sx={{
                                color: BRAND_LUXE_ERROR,
                                textTransform: 'none',
                                fontSize: 13,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                border: `1px solid ${BRAND_LUXE_ERROR}33`,
                                borderRadius: 99,
                                px: 3,
                                py: 1,
                            }}>
                            Cancel Subscription
                        </Button>
                    </>
                )}
            </Box>

            <Dialog
                open={cancelOpen}
                onClose={() => !canceling && setCancelOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: subscriptionDialogPaperSx }}>
                <DialogTitle sx={subscriptionDialogTitleSx}>Cancel subscription?</DialogTitle>
                <DialogContent sx={{ pt: 0 }}>
                    <Typography sx={subscriptionDialogBodySx}>
                        Your plan stays active until the end of the current billing period unless you cancel
                        immediately.
                    </Typography>
                </DialogContent>
                <DialogActions sx={subscriptionDialogActionsSx}>
                    <Button
                        onClick={() => {
                            trackSubscriptionAction(MIXPANEL_EVENT.KEEP_SUBSCRIPTION_CLICK, {
                                buttonName: 'KEEP PLAN',
                                buttonAction: 'KEEP_SUBSCRIPTION',
                                subscriptionType: 'campaign_host',
                                subscriptionId: subscription._id,
                            });
                            setCancelOpen(false);
                        }}
                        disabled={canceling}
                        sx={{
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            color: 'rgba(255,255,255,0.6)',
                        }}>
                        Keep plan
                    </Button>
                    <Button
                        onClick={() => handleCancel(false)}
                        disabled={canceling}
                        sx={{
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            color: BRAND_LUXE_PRIMARY,
                        }}>
                        End of period
                    </Button>
                    <Button
                        onClick={() => handleCancel(true)}
                        disabled={canceling}
                        sx={{
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            color: BRAND_LUXE_ERROR,
                            minWidth: 100,
                        }}>
                        {canceling ? <CircularProgress size={20} sx={{ color: BRAND_LUXE_ERROR }} /> : 'Cancel now'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: subscriptionDialogPaperSx }}>
                <DialogTitle sx={subscriptionDialogTitleSx}>Billing history</DialogTitle>
                <DialogContent
                    dividers
                    sx={{
                        borderColor: `${BRAND_OUTLINE_VARIANT}44`,
                        '&.MuiDialogContent-dividers': {
                            borderTopColor: `${BRAND_OUTLINE_VARIANT}44`,
                            borderBottomColor: `${BRAND_OUTLINE_VARIANT}44`,
                        },
                    }}>
                    {allHistoryItems.map(item => (
                        <Box
                            key={item.id}
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                py: 1.5,
                                borderBottom: `1px solid ${BRAND_OUTLINE_VARIANT}33`,
                            }}>
                            <Box>
                                <Typography sx={{ fontWeight: 600, color: '#fff', fontFamily: FONT_ENUM.MAIN_BOLD }}>
                                    {item.description ?? 'Payment'}
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                                    {formatSubscriptionDate(item.date)}
                                </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography sx={{ color: '#fff', fontFamily: FONT_ENUM.MAIN_BOLD }}>
                                    {formatBillingHistoryAmount(item)}
                                </Typography>
                                {(item.hosted_invoice_url || item.invoice_pdf) && (
                                    <Button
                                        size="small"
                                        onClick={() =>
                                            window.open(item.hosted_invoice_url ?? item.invoice_pdf ?? '', '_blank')
                                        }
                                        sx={{
                                            textTransform: 'none',
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: BRAND_LUXE_PRIMARY,
                                            fontSize: 12,
                                        }}>
                                        Invoice
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    ))}
                </DialogContent>
                <DialogActions sx={subscriptionDialogActionsSx}>
                    <Button
                        onClick={() => setHistoryOpen(false)}
                        sx={{
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            color: BRAND_LUXE_PRIMARY,
                        }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
